import { FC, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { isNotEmpty } from '../../../../../_dashboard/helpers'
import { BookingExtra } from '../core/_models'
import clsx from 'clsx'
import { useListView } from '../core/ListViewProvider'
import { DataLoading } from '../../../../../_dashboard/partials/components/loading/DataLoading'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { createOne, getAll, getOneByIdbylang, updateOne } from '../../../../services/common_requests'
import { useIntl } from 'react-intl'
import { useQueryRequest } from '../core/QueryRequestProvider'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

type Props = {
  isBookingExtraLoading: boolean
  BookingExtra: BookingExtra
}

const editBookingExtraSchema = Yup.object().shape({
  translations: Yup.array().of(
    Yup.object().shape({
      title: Yup.string()
        .min(3, 'Minimum 3 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('Title is required'),
    })
  ),
})

const BookingExtraEditModalForm: FC<Props> = ({ BookingExtra, isBookingExtraLoading }) => {
  const { itemIdForUpdate, setItemIdForUpdate } = useListView()
  const { refetchData } = useQueryResponse()
  const { state } = useQueryRequest();
  const [query, setQuery] = useState<any>(state);
  const intl = useIntl()
  const [BookingExtraForEdit] = useState<BookingExtra>({
    ...BookingExtra,
    translations: BookingExtra.translations ?? [{ language: 'en', title: '' }],
    type: BookingExtra.type ?? 'checkbox',
    category: BookingExtra.category ?? '',
    isActive: BookingExtra.isActive ?? true,
    order: BookingExtra.order ?? 0,
  })
  const LANGUAGE_NAMES: { [key: string]: string } = {
    en: intl.formatMessage({ id: 'COMMON.ENGLISH_LANGUAGE' }),
    fr: intl.formatMessage({ id: 'COMMON.FRENSH_LANGUAGE' }),
    es: intl.formatMessage({ id: 'COMMON.ESPANGOL_LANGUAGE' }),
    ar: intl.formatMessage({ id: 'COMMON.ARABIC_LANGUAGE' }),
  };

  const [languages, setLanguages] = useState<{ code: string; name: string }[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  useEffect(() => {
    if (languages.length > 0) {
      setSelectedLanguage(languages[0].code);
    }
  }, [languages]);
  useEffect(() => {
    getAll(query, '/languages').then((data) => {
      if (data && Array.isArray(data)) {
        const formattedLanguages = data.map((code: string) => ({
          code,
          name: LANGUAGE_NAMES[code] || code.toUpperCase(),
        }));
        setLanguages(formattedLanguages);
      }
    });
  }, [query]);

  useEffect(() => {
    if (itemIdForUpdate && selectedLanguage) {
      getOneByIdbylang(`/booking-extras/${itemIdForUpdate}?language=${selectedLanguage}`)
        .then((data) => {
          if (data && data.translations) {
            // Mettre à jour toutes les traductions
            formik.setValues({
              ...formik.values,
              translations: data.translations.map((t: any) => ({
                ...t,
                _id: t._id
              }))
            });
          }
        })
        .catch((error) => console.error('Error fetching translation:', error));
    }
  }, [selectedLanguage, itemIdForUpdate]);






  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetchData()
    }
    setItemIdForUpdate(undefined)
  }

  const formik = useFormik({

    initialValues: BookingExtraForEdit,
    validationSchema: editBookingExtraSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      if (isNotEmpty(itemIdForUpdate)) {
        const selectedTranslation = values.translations.find(t => t.language === selectedLanguage);

        if (!selectedTranslation || !selectedTranslation._id) {
          toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ID_NOT_FOUND' }));
          return;
        }
        const payload = {
          title: values.translations[0].title,
          translations: values.translations.map(translation => ({
            language: translation.language || 'en',
            title: translation.title,
          })),
          type: values.type,
          category: values.category,
          isActive: values.isActive,
          order: values.order,
        };

        await updateOne(payload, `/booking-extras/${itemIdForUpdate}`).then((response) => {
          if (response?.payload?.code === 2021) {
            toast.success(
              intl.formatMessage({
                id: 'ADMIN.COMMON.SUCCESS.OPTION_UPDATED_SUCCESSFULLY',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            );
            cancel(true)
            refetchData()
          }
          else if (response?.payload?.code === 4401) {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.ID_NOT_EXIST',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            );
          }
          else if (response?.payload?.code === 9100) {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.ID_NOT_FOUND',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            );
          }
          else {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_OPTION',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            );
          }
        })

      }
      else {
        const payload = {
          title: values.translations[0].title,
          translations: values.translations.map(translation => ({
            language: translation.language || 'en',
            title: translation.title,
          })),
          type: values.type,
          category: values.category,
          isActive: values.isActive,
          order: values.order,
        };
        try {

          await createOne(payload, '/booking-extras').then((response) => {
            if (response?.payload?.code === 2021) {

              toast.success(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.SUCCESS.OPTION_ADDED_SUCCESSFULLY',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              );
              cancel(true)
            }
            else if (response?.payload?.code === 4400) {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.NAME_ALREADY_EXIST',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              );
            }
            else {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_OPTION',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              );
            }
          })
        }
        catch (ex) {
          console.error(ex)
        } finally {
          setSubmitting(true)
          cancel(true)
        }
      }
    },
  })
  const selectedTranslation = formik.values.translations.find(t => t.language === selectedLanguage);

  return (
    <>
      <form
        id='kt_modal_add_booking-extra_form'
        className='form'
        onSubmit={formik.handleSubmit}
        noValidate
      >
        {/* begin::Scroll */}
        <div
          className='d-flex flex-column scroll-y me-n15 pe-6'
          id='kt_modal_add_booking-extra_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_booking-extra_header'
          data-kt-scroll-wrappers='#kt_modal_add_BookingExtra_scroll'
          data-kt-scroll-offset='300px'
        >
          <div className='fv-row mb-4'>
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COLUMNS.LANGUAGE' })}
            </label>
            {formik.values.translations.map((translation, index) => (
              <div key={index}>
                <select
                  id={`translations[${index}].language`}
                  name={`translations[${index}].language`}
                  onChange={(e) => {
                    formik.handleChange(e);
                    setSelectedLanguage(e.target.value); // Met à jour la langue sélectionnée
                  }}
                  value={translation.language}

                  className='form-select'
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                {formik.errors.translations && (formik.errors.translations[index] as { language?: string })?.language && (
                  <div>{(formik.errors.translations[index] as { language: string }).language}</div>
                )}
              </div>
            ))}
          </div>
          {/* begin::Input group */}
          <div className='fv-row mb-4'>
            {/* begin::Label */}
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COLUMNS.NAME' })}
            </label>
            {/* end::Label */}

            {/* begin::Input */}
            <input
              placeholder={intl.formatMessage({ id: 'COLUMNS.NAME' })}
              value={selectedTranslation?.title || ''}
              onChange={(e) => {
                const index = formik.values.translations.findIndex(t => t.language === selectedLanguage);
                if (index !== -1) {
                  formik.setFieldValue(`translations[${index}].title`, e.target.value);
                }
              }}
              type="text"
              name="title"
              className={clsx(
                'form-control mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.translations?.[0]?.title && (formik.errors.translations?.[0] as { title?: string })?.title },
                { 'is-valid': formik.touched.translations?.[0]?.title && !(formik.errors.translations?.[0] as { title?: string })?.title }
              )}
              autoComplete="off"
              disabled={formik.isSubmitting || isBookingExtraLoading}
            />


            {/* end::Input */}
          </div>
          <div className='fv-row mb-4'>
            <label className='required fw-bold fs-6 mb-2'>Type</label>
            <select
              className='form-select'
              value={formik.values.type}
              onChange={(e) => formik.setFieldValue('type', e.target.value)}
            >
              <option value='checkbox'>checkbox</option>
              <option value='radio'>radio</option>
              <option value='text'>text</option>
            </select>
          </div>
          <div className='fv-row mb-4'>
            <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'COLUMNS.CATEGORY' })}</label>
            <input
              className='form-control'
              value={formik.values.category || ''}
              onChange={(e) => formik.setFieldValue('category', e.target.value)}
            />
          </div>
          <div className='fv-row mb-4'>
            <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'COLUMNS.ORDER' })}</label>
            <input
              type='number'
              className='form-control'
              value={formik.values.order ?? 0}
              onChange={(e) => formik.setFieldValue('order', Number(e.target.value))}
            />
          </div>
          <div className='form-check form-switch mb-4'>
            <input
              className='form-check-input'
              type='checkbox'
              checked={formik.values.isActive}
              onChange={() => formik.setFieldValue('isActive', !formik.values.isActive)}
            />
            <label className='form-check-label'>{intl.formatMessage({ id: 'COLUMNS.STATUS' })}</label>
          </div>
          {/* end::Input group */}
        </div>
        {/* end::Scroll */}

        {/* begin::Actions */}
        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            data-kt-booking-extras-modal-action='cancel'
            disabled={formik.isSubmitting || isBookingExtraLoading}
          >
            {intl.formatMessage({ id: 'HEADER.RESET' })}
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            data-kt-booking-extras-modal-action='submit'
            disabled={
              isBookingExtraLoading || formik.isSubmitting || !formik.isValid || !formik.touched
            }
          >
            <span className='indicator-label'>           {intl.formatMessage({ id: 'HEADER.SAVE' })}
            </span>
            {(formik.isSubmitting || isBookingExtraLoading) && (
              <span className='indicator-progress'>
                {intl.formatMessage({ id: 'COMMON.PLEASE_WAIT' })}{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Actions */}
      </form>
      {(formik.isSubmitting || isBookingExtraLoading) && <DataLoading />}
    </>
  )
}

export { BookingExtraEditModalForm }
